/* eslint-disable no-use-before-define */
import {
  defaultScheduleDay,
  validateScheduleDay
} from '@ps-design-system/components/ScheduleInputField';
import { useAsyncLoadingFn } from '@ps-design-system/lib/hooks/use-async-loading-fn';
import { isEqual } from 'lodash';
import cloneDeep from 'lodash/cloneDeep';
import PropTypes from 'prop-types';
import * as React from 'react';
import { useCheckoutForms } from './checkout-forms/use-checkout-forms';
import {
  createCheckoutFormDefaultValues,
  createQuotePayload,
  getErrorMsg,
  getPaymentAmount
} from './checkout-utils';
import { trackEvent } from './services/analytics-service';
import {
  fetchBookingQuote,
  handleBookingRequestCheckout,
  postBillingAddress
} from './services/checkout-service';
import { fetchIDVInfo } from './services/identity-verification-service';
import {
  createStripePaymentMethod,
  pollPaymentProcessingStatus
} from './services/payment-service';
import { NEW_CARD_OPTION } from './checkout-forms/PaymentForm/CreditCardSelectField';
import { StripeElementsContextProvider } from './StripeElementsContextProvider';
import { useStripeInstanceContext } from './StripeInstanceContextProvider';

export const CheckoutContext = React.createContext({});

export const CheckoutContextProvider = ({
  accessToken,
  initialQuote,
  initialError,
  initialBookingId,
  inventory,
  listing,
  customerPaymentMethods,
  customerBillingAddress,
  SSOid,
  children,
  intl
}) => {
  const [IDVInfo, setIDVInfo] = React.useState(null);
  const [stripeElements, setStripeElements] = React.useState(null);

  const [quote, setQuote] = React.useState(initialQuote);
  const { spaceRentalContract } = quote || {};
  const { booking, risk } = spaceRentalContract?.meta || {};

  const { stripeInstance } = useStripeInstanceContext();

  const [errorMsg, setErrorMsg] = React.useState('');
  const _setErrorMsg = (...args) => {
    setErrorMsg(...args);
  };

  const [paymentProcessing, setPaymentProcessing] = React.useState(false);

  const updateIDVInfo = async ({ bookingDetails }) => {
    try {
      const IDVPayload = {
        sso_id: SSOid,
        listing_id: listing?._id,
        schedule: bookingDetails?.schedule,
        alcohol_consumed: bookingDetails?.alcoholConsumed,
        booking: {
          ...booking,
          schedule: bookingDetails?.schedule,
          number_of_guests: parseInt(bookingDetails?.totalGuests ?? 0, 10),
          tags: {
            activity: bookingDetails?.activity
          },
          alcohol_consumed: bookingDetails?.alcoholConsumed
        },
        activity: bookingDetails?.activity
      };

      const IDVInfo = await fetchIDVInfo(accessToken, IDVPayload);

      setIDVInfo(IDVInfo);
    } catch (error) {
      // @TODO: handle error - check with product/design
    }
  };

  React.useEffect(() => {
    updateIDVInfo(bookingDetailsForm.getValues());
  }, []);

  const handleScheduleError = (error, validScheduleDays) => {
    if (validScheduleDays.length > 0) {
      // if server returns a schedule error when extending a day,
      // clear the last schedule date
      const lastValidDay = validScheduleDays[validScheduleDays.length - 1];
      const newScheduleDay = cloneDeep(defaultScheduleDay);
      newScheduleDay.start.date = lastValidDay?.start?.date;
      bookingDetailsForm.setValue('schedule', [
        ...validScheduleDays.slice(0, -1),
        newScheduleDay
      ]);
    }

    // set error message on the last schedule day.
    // this error is cleared in BookingScheduleInput when the schedule changes
    bookingDetailsForm.setError(
      `schedule.${(validScheduleDays.length || 1) - 1}.start.time`,
      {
        type: 'manual',
        message: error.conflict || error.invalid || ''
      }
    );
  };

  const handleCreditsError = (error) => {
    // if user updates the quote after they've applied credits,
    // the server may respond with an error due to a negative subtotal.
    // in this case the backend provides a suggested amount of credits to apply
    // which we use to request a new quote.
    // @TODO: do all this on the backend
    const suggestedCredits =
      error?.guest_payment_method_details?.suggested?.subtotal_ps_credit;
    if (suggestedCredits > 0) {
      // this will trigger a new quote request
      paymentForm.setValue('totalCreditsApplied', suggestedCredits);
    }
  };

  const prevBillingAddress = React.useRef();
  const prevQuotePayload = React.useRef();

  const [updateQuote, isFetchingQuote] = useAsyncLoadingFn(
    async (checkoutFormValues) => {
      const validScheduleDays = (
        checkoutFormValues?.bookingDetails?.schedule || []
      ).filter((day) => validateScheduleDay(day));

      if (!validScheduleDays.length) {
        return;
      }

      const quotePayload = createQuotePayload({
        listing,
        SSOid,
        formValues: {
          ...(checkoutFormValues || {}),
          bookingDetails: {
            ...(checkoutFormValues?.bookingDetails || {}),
            schedule: validScheduleDays
          }
        }
      });

      const isPayloadChanged = !isEqual(quotePayload, prevQuotePayload.current);
      const isBillingChanged = !isEqual(checkoutFormValues?.billingAddress, prevBillingAddress.current);

      if (!isPayloadChanged && !isBillingChanged) {
        // this can happen when a schedule error occurs
        // and the last schedule day is cleared

        // billing address is not part of quote payload,
        // but it's required dependency so we shouldn't skip quote update
        return;
      }

      try {
        const newQuote = await fetchBookingQuote(accessToken, quotePayload);

        trackEvent('booking price created', {
          is_single_page_checkout: true,
          listing_id: listing?._id,
          booking_id: newQuote?.spaceRentalContract?.meta?.booking?._id,
          sso_id: SSOid
        });

        prevQuotePayload.current = cloneDeep(quotePayload);
        prevBillingAddress.current = cloneDeep(checkoutFormValues?.billingAddress);
        setQuote(newQuote);
      } catch (error) {
        const scheduleError = error?.body?.body?.errors?.find(
          ({ errorId }) =>
            errorId === 'ERROR_SPACE_IS_UNAVAILABLE' ||
            errorId === 'ERROR_END_TIME_AT_LEAST_1_HR' ||
            errorId === 'ERROR_OVERLAPPING_DATES'
        );
        if (scheduleError) {
          handleScheduleError(scheduleError, validScheduleDays);
          return;
        }

        if (
          error?.error_code === 'subtotal_remaining_negative' &&
          paymentForm.getValues('totalCreditsApplied') > 0
        ) {
          handleCreditsError(error);
          return;
        }

        // some unknown error occurred
        setErrorMsg(getErrorMsg(error, intl));
      }
    }
  );

  React.useEffect(() => {
    if (!initialError) {
      return;
    }

    // initial request for a quote can contain a schedule error
    const scheduleError = initialError?.body?.body?.errors?.find(
      ({ errorId }) =>
        errorId === 'ERROR_SPACE_IS_UNAVAILABLE' ||
        errorId === 'ERROR_END_TIME_AT_LEAST_1_HR'
    );

    if (scheduleError) {
      setTimeout(() => {
        // form needs to mount before we can set the error
        handleScheduleError(scheduleError, []);
      });
    }
  }, []);

  const handleFormFieldChange = async (newValues, changedFields) => {
    const idvUpdateFields = [
      'schedule',
      'activity',
      'alcoholConsumed',
      'totalGuests'
    ];
    if (
      (changedFields || []).some((field) => idvUpdateFields.includes(field))
    ) {
      updateIDVInfo(newValues);
    }

    const billingAddressUpdateFields = ['country', 'state'];
    if (
      (changedFields || []).some((field) =>
        billingAddressUpdateFields.includes(field)
      )
    ) {
      // country and state changes can affect tax rates
      // so we need to save the billing address and request a new quote
      await postBillingAddress(accessToken, SSOid, newValues.billingAddress);
      updateQuote(newValues);
      return;
    }

    const quoteUpdateFields = [
      'schedule',
      'totalGuests',
      'addOns',
      'selectedPaymentOption',
      'totalCreditsApplied'
    ];
    if (
      (changedFields || []).some((field) => quoteUpdateFields.includes(field))
    ) {
      updateQuote(newValues);
    }
  };

  const {
    bookingDetailsForm,
    paymentForm,
    socialEventPolicyForm,
    billingAddressForm,
    billingCountries,
    identityVerificationForm,
    submitForms
  } = useCheckoutForms({
    intl,
    onChange: handleFormFieldChange,
    defaultValues: createCheckoutFormDefaultValues({
      quote,
      inventory,
      customerPaymentMethods,
      customerBillingAddress
    })
  });

  const collectFormsValues = async () => {
    return new Promise(async (resolve) => {
      try {
        const formValues = await submitForms(
          {
            socialEventPolicy:
              risk?.guest_social_events_policy_agreement_required,
            payment: quote?.guestPaymentDetails?.subtotal_remaining > 0,
            billingAddress: !customerBillingAddress?.has_full_address,
            identityVerification: IDVInfo?.guest_needs_verification
          },
          customerPaymentMethods?.[0]
        );
        resolve(formValues);
      } catch (e) {
        return resolve();
      }
    });
  };

  const getPaymentMethodId = async () => {
    const subtotalRemaining =
      quote?.guestPaymentDetails?.subtotal_remaining;

    const selectedPaymentMethod = paymentForm.getValues('selectedPaymentMethod');
    const selectedCard = paymentForm.getValues('selectedCreditCard');
    const cardholderName = paymentForm.getValues('cardholderName');
    const isNewCard = selectedCard?.id === NEW_CARD_OPTION.id;

    const isFullCreditsPayment = 0 === subtotalRemaining;
    const isCreditCardPayment = !selectedPaymentMethod || selectedPaymentMethod === 'card';

    // Checkout with an existing user's card
    if ((isCreditCardPayment && selectedCard && !isNewCard) || isFullCreditsPayment) {
      const defaultCard = customerPaymentMethods?.[0];
      const card = selectedCard || defaultCard;
      const paymentMethodId = card?.id;

      if (!paymentMethodId) {
        throw new Error('No Payment Method');
      }

      return paymentMethodId;
    }

    // Checkout with new payment info from stripe element (new card, apple/google pay etc.)
    const paymentMethodResponse = await createStripePaymentMethod({
      stripeInstance,
      stripeElements,
      cardholderName
    });
    const paymentMethodId = paymentMethodResponse?.paymentMethod?.id;
    if (paymentMethodResponse?.error) {
      const errorMsg = paymentMethodResponse?.error?.message || 'Please try correcting your card details and then resubmit.';
      throw new Error(errorMsg);
    }
    return paymentMethodId;
  };

  const handleRequestError = (error) => {
    trackEvent('booking request error', {
      listing_id: listing?._id,
      sso_id: SSOid,
      error_message: getErrorMsg(error, intl),
      is_single_page_checkout: true
    });

    setPaymentProcessing(false);
    setErrorMsg(getErrorMsg(error, intl));
  };

  /**
   * Handles the booking request submission process, including form validation,
   * request payload creation, and sending the booking request.
   * If an error occurs, a message is set in the context state, which triggers the error dialog
   */
  const [submitRequest, isSubmittingRequest] = useAsyncLoadingFn(async (values) => {
    try {
      // If values are not passed collect them here
      const formValues = values || await collectFormsValues();
      if (!formValues) {
        return;
      }

      if (!customerBillingAddress?.has_full_address) {
        try {
          // Billing address update success is required in order to create booking
          await postBillingAddress(
            accessToken,
            SSOid,
            billingAddressForm.getValues()
          );
        } catch (error) {
          // postBillingAddress address error
          return;
        }
      }

      trackEvent('booking request attempt', {
        listing_id: listing?._id,
        sso_id: SSOid,
        booking_id: booking?._id,
        is_single_page_checkout: true
      });

      try {
        const paymentMethodId = await getPaymentMethodId();

        const bookingResponse = await handleBookingRequestCheckout({
          accessToken,
          SSOid,
          listing,
          booking,
          formValues,
          paymentMethodId,
          quote,
          initialBookingId
        });

        setPaymentProcessing(true);
        const paymentProcessed = await pollPaymentProcessingStatus(
          accessToken,
          bookingResponse?.spaceRentalContract?.booking_id,
          stripeInstance
        );

        if (paymentProcessed?.error || paymentProcessed?.statusCode >= 400) {
          throw paymentProcessed.error;
        }

        trackEvent('booking requested', {
          listing_id: listing?._id,
          sso_id: SSOid,
          booking_id: booking?._id,
          is_single_page_checkout: true
        });

        return bookingResponse;
      } catch (e) {
        handleRequestError(e);
      }
    } catch (e) {
      // validateBeforeSubmit error
    }
  });

  const amount = getPaymentAmount(quote, paymentForm.watch('selectedPaymentOption'));
  const currency = spaceRentalContract?.meta?.listing?.prices[0]?.currency;
  const country = spaceRentalContract?.meta?.listing?.country;

  return (
    <CheckoutContext.Provider
      value={{
        listing,
        quote,
        inventory,
        errorMsg,
        setErrorMsg: _setErrorMsg,
        customerPaymentMethods,
        IDVInfo,
        bookingDetailsForm,
        paymentForm,
        billingAddressForm,
        socialEventPolicyForm,
        identityVerificationForm,
        billingCountries,
        customerBillingAddress,
        submitRequest,
        isSubmittingRequest,
        isFetchingQuote,
        paymentProcessing,
        collectFormsValues
      }}
    >
      <StripeElementsContextProvider
        amount={amount}
        country={country}
        currency={currency}
        onStripeElementsCreated={setStripeElements}
      >
        {children}
      </StripeElementsContextProvider>
    </CheckoutContext.Provider>
  );
};

CheckoutContextProvider.displayName = 'CheckoutContextProvider';
CheckoutContextProvider.propTypes = {
  accessToken: PropTypes.string,
  initialQuote: PropTypes.object,
  initialError: PropTypes.object,
  initialBookingId: PropTypes.string,
  inventory: PropTypes.array,
  listing: PropTypes.object,
  customerPaymentMethods: PropTypes.array,
  customerBillingAddress: PropTypes.object,
  SSOid: PropTypes.string,
  children: PropTypes.node,
  intl: PropTypes.object
};

export const useCheckoutContext = () => {
  const context = React.useContext(CheckoutContext);
  if (!context) {
    throw new Error(
      'useCheckoutContext must be used within a CheckoutContextProvider'
    );
  }
  return context;
};
